import { patternCatalog, totalPatternCount } from '../data/patternCatalog.js';
import { audio } from '../systems/audio/AudioSystem.js';

/**
 * Card practice pick screen (BULLET-HELL.md §D8).
 *
 * Boss Challenge's unit is the fight; danmaku's onboarding unit is the *card*.
 * In a real fight a given spell card comes around once every few minutes, and
 * a player who dies to Apotheosis has to clear a boss to see it again — which
 * is the reason a hard card stays unlearned, not any property of the card.
 *
 * The list is entirely data that already existed: data/patternCatalog.js for
 * the names (instantiated from the defs, so it can't drift from what a boss
 * actually plays) and `statistics.patternRecords` for the record, which D1
 * has been writing since it shipped — including in the rewardless modes,
 * specifically so this screen would open with history already in it.
 *
 * Gated by the act spoiler bar, the same as act select and the codex: a save
 * that hasn't cleared Act I sees no Act II or Act III cards here, and the
 * "captured N of M" denominator counts only what it is showing (see
 * patternCatalog's `statistics` argument). Listing "The Last Verse" to a
 * first-run player would say more about the ending than the act select screen
 * is willing to.
 *
 * Uncaptured-only is the default view because it is the question the mode
 * exists to answer. The toggle is there because "run the one I like" is also
 * a legitimate reason to be here, and a list that hides everything you've
 * beaten stops being a list of the game's cards.
 */
export class PatternPracticeMenu {
  constructor(root, save, { onPick, onBack }) {
    this.root = root;
    this.save = save;
    this.onPick = onPick;
    this.onBack = onBack;
    this._uncapturedOnly = true;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay statistics-overlay practice-select-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _records() {
    return this.save.statistics.patternRecords ?? {};
  }

  _render() {
    this.el.innerHTML = '';
    const records = this._records();
    const total = totalPatternCount(this.save.statistics);
    const captured = Object.values(records).filter((r) => (r?.captures ?? 0) > 0).length;

    const header = document.createElement('div');
    header.innerHTML = `
      <div class="menu-kicker">CARD PRACTICE ${Math.min(captured, total)}/${total}</div>
      <div class="menu-title">SPELL CARDS</div>
      <div class="menu-subtitle">Drill one attack on repeat against a boss that cannot be killed. Captures count; nothing else pays.</div>
    `;
    this.el.appendChild(header);

    const filter = document.createElement('button');
    filter.type = 'button';
    filter.className = 'menu-button practice-filter-button';
    filter.textContent = this._uncapturedOnly ? 'Showing: not yet captured' : 'Showing: every card';
    filter.addEventListener('click', () => {
      audio.playSfx('click');
      this._uncapturedOnly = !this._uncapturedOnly;
      this._render();
    });
    this.el.appendChild(filter);

    const list = document.createElement('div');
    list.className = 'shop-rows achievement-rows capture-rows';
    let shown = 0;
    for (const { index, bossName, patterns } of patternCatalog(this.save.statistics)) {
      const visible = this._uncapturedOnly
        ? patterns.filter((name) => (records[name]?.captures ?? 0) === 0)
        : patterns;
      if (!visible.length) continue;
      const done = patterns.filter((name) => (records[name]?.captures ?? 0) > 0).length;
      const heading = document.createElement('div');
      heading.className = 'achievement-group-heading';
      heading.innerHTML = `${bossName} <small>${done}/${patterns.length}</small>`;
      list.appendChild(heading);
      for (const name of visible) {
        list.appendChild(this._buildRow(index, name, records[name]));
        shown++;
      }
    }
    if (!shown) {
      const empty = document.createElement('div');
      empty.className = 'menu-subtitle';
      // Only reachable with the filter on, and only by a player who has
      // captured all of them — which deserves to be said out loud.
      empty.textContent = 'Every card in the game captured. Switch the filter to drill one again.';
      list.appendChild(empty);
    }
    this.el.appendChild(list);

    const hint = document.createElement('div');
    hint.className = 'scroll-hint';
    hint.textContent = '▼ Scroll to browse';
    this.el.appendChild(hint);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  _buildRow(bossIndex, name, record) {
    const { attempts = 0, captures = 0 } = record ?? {};
    const done = captures > 0;
    const row = document.createElement('button');
    row.type = 'button';
    row.className = `achievement-row practice-row${done ? ' achievement-row-unlocked' : ''}`;
    row.innerHTML = `
      <span class="achievement-name">${name}</span>
      <span class="achievement-hint">${attempts ? `${captures} captured of ${attempts} faced` : 'never faced'}</span>
      <span class="achievement-echoes">${done ? '✓' : '—'}</span>
    `;
    row.addEventListener('click', () => {
      audio.playSfx('click');
      this.onPick(bossIndex, name);
    });
    return row;
  }

  destroy() {
    this.el.remove();
  }
}
